import clsx from 'clsx'
import {
  type InputHTMLAttributes,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import ReactInputMask from 'react-input-mask'

import { useDebounce } from '../hooks/useDebounce'

type SearchInputProps = InputHTMLAttributes<HTMLInputElement> & {
  disabled?: boolean
  placeholder: string
  defaultSearchTerm?: string
  mask?: string
  onSearch: (searchedTherm: string) => void
  className?: string
}

const SearchInput = forwardRef(
  (
    {
      placeholder,
      defaultSearchTerm = '',
      disabled = false,
      className,
      mask,
      onSearch,
      ...rest
    }: SearchInputProps,
    ref?: React.Ref<{ clear: () => void } | undefined>
  ) => {
    const [searchTherm, setSearchTherm] = useState(defaultSearchTerm)
    const debouncedValue = useDebounce(searchTherm, 500)
    const isFirstRender = useRef(true)

    useEffect(() => {
      if (!isFirstRender.current) {
        onSearch(debouncedValue)
      }

      return () => {
        isFirstRender.current = false
      }
    }, [isFirstRender, debouncedValue])

    useImperativeHandle(ref, () => {
      if (!ref) {
        return
      }

      return {
        clear: () => setSearchTherm(''),
      }
    }, [])

    return (
      <div
        className={clsx(
          'flex items-center h-10 gap-2 px-4 rounded-md bg-grey-50',
          {
            'opacity-50': disabled,
          },
          className
        )}
      >
        <span className="flex items-center justify-center h-full w-7">
          <MagnifyingGlassIcon className="w-5 h-5 text-grey-700" />
        </span>
        <ReactInputMask
          value={searchTherm}
          onChange={(e) => setSearchTherm(e.target.value)}
          mask={mask || ''}
          maskPlaceholder={null}
          disabled={disabled}
        >
          <input
            placeholder={placeholder}
            className="w-full h-full text-sm font-normal outline-none text-grey-900 placeholder:text-grey-700 bg-grey-50"
            {...rest}
          />
        </ReactInputMask>
      </div>
    )
  }
)

SearchInput.displayName = 'SearchInput'

export { SearchInput }
